import { Rectangle, Sprite, Texture } from 'pixi.js';
import { projectDirection, ISO } from './camera.js';
import { PEOPLE_DIRECTIONS, PEOPLE_FRAMES, PEOPLE_SCALE, type PeopleAction } from './people-frames.js';

/** Animated 8-way characters rendered by minisprite, one atlas per airport-people cell (tools/pack-people.py). */
export type { PeopleAction };
export const CAPTAIN_CELL = 15;
export const PILOT_CELLS = [12, 13];
export const STAFF_CELL = 14;
export function peopleAnimationTick(action: PeopleAction, frame: number, phase = 0): number {
    return (frame + phase) / (action === 'run' ? 4.5 : action === 'walk' ? 5 : 6);
}
/** On-screen height of a standing figure is ~52px; the renders are PEOPLE_SCALE of minisprite's 1024px frame. */
const DISPLAY = 0.078 / PEOPLE_SCALE;
const PASSENGER_CELLS = Array.from({ length: 12 }, (_, i) => i);
const textures = new Map<string, Texture>();

export const peopleAtlas = (cell: number) => `/assets/art/people/${cell}.webp`;
export const peopleAtlases = () => Object.keys(PEOPLE_FRAMES).map(cell => peopleAtlas(Number(cell)));

/** The rendered cell for a passenger look, stepping to the next finished render when one is missing. */
export function passengerCell(look: number) {
    for (let i = 0; i < PASSENGER_CELLS.length; i++) {
        const cell = PASSENGER_CELLS[(look + i) % PASSENGER_CELLS.length];
        if (PEOPLE_FRAMES[cell]) return cell;
    }
    return CAPTAIN_CELL;
}

/** minisprite names directions for the world under the default camera (south looks screen down-left), so a
 *  world vector is turned into that frame by undoing the current camera's projection. */
export function peopleDirection(worldX: number, worldY: number) {
    const s = projectDirection(worldX, worldY), u = s.x / ISO, v = s.y * 2 / ISO;
    const east = (u + v) / 2, south = (v - u) / 2;
    // 0 = south, then counter-clockwise on screen: south_east, east, north_east, ...
    const sector = Math.round(Math.atan2(east, south) / (Math.PI / 4));
    return PEOPLE_DIRECTIONS[(sector + 8) % 8];
}

function texture(cell: number, action: PeopleAction, direction: string, frame: number) {
    const key = `${cell}/${action}/${direction}/${frame}`;
    const cached = textures.get(key);
    if (cached) return cached;
    const a = PEOPLE_FRAMES[cell][action], row = PEOPLE_DIRECTIONS.indexOf(direction as typeof PEOPLE_DIRECTIONS[number]);
    const made = new Texture(Texture.from(peopleAtlas(cell)).baseTexture, new Rectangle(a.x + frame * a.w, a.y + row * a.h, a.w, a.h));
    textures.set(key, made);
    return made;
}

/** Shows `cell` doing `action` along a world direction; `tick` advances the animation. */
export function posePerson(image: Sprite, cell: number, action: PeopleAction, worldX: number, worldY: number, tick: number) {
    const a = PEOPLE_FRAMES[cell]?.[action] ?? PEOPLE_FRAMES[cell]?.idle;
    if (!a) return;
    image.texture = texture(cell, PEOPLE_FRAMES[cell][action] ? action : 'idle', peopleDirection(worldX, worldY), Math.floor(tick) % a.frames);
    image.anchor.set(a.anchor[0] / a.w, a.anchor[1] / a.h);
}

export function personSprite(cell: number): Sprite {
    const image = new Sprite();
    image.scale.set(DISPLAY);
    posePerson(image, cell, 'idle', 1, 1, 0);
    return image;
}
